import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy } from '@ngneat/until-destroy';
import { combineLatest, map, Observable, startWith, tap } from 'rxjs';

import { TimeZonesApiService, TimeZonesList } from '@blaze-shared/platform-data-access';

import {
  CellValueUpdateFromTimeZone,
  type InlineEditorConfig,
  type InlineEditorLayoutMode,
} from '../base-editor/inline-editor.types';

@UntilDestroy()
@Component({
  selector: 'be-time-zone-menu',
  templateUrl: './time-zone-menu.component.html',
  styleUrls: ['./time-zone-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TableTimeZoneMenuComponent implements AfterViewInit {
  private readonly timeZonesApiService = inject(TimeZonesApiService);

  valueChange = output<CellValueUpdateFromTimeZone | string | undefined>();

  @Input() config?: InlineEditorConfig;
  @Input() layoutMode?: InlineEditorLayoutMode;
  @Input() value: string | number | string[] | null | undefined;

  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger | undefined;
  @ViewChild('filterInput') filterInput!: ElementRef<HTMLInputElement>;

  filteredRecords$!: Observable<TimeZonesList | null>;
  searchFormCtrl = new UntypedFormControl();

  availableTimeZones$: Observable<TimeZonesList | null>;
  pickedVal?: string;

  get isInputLike(): boolean {
    return this.layoutMode === 'input-like';
  }

  constructor() {
    this.availableTimeZones$ = this.timeZonesApiService.timeZones$;
    this.filteredRecords$ = combineLatest([
      this.searchFormCtrl.valueChanges.pipe(
        startWith(''),
        map((v) => v.toLowerCase()),
      ),
      this.availableTimeZones$,
    ]).pipe(
      map(([searchValue, timezones]) => {
        const tzList = (timezones || [])?.filter(
          (v) =>
            v.label.toLowerCase().includes(searchValue) ||
            v.value.toLowerCase().includes(searchValue),
        );
        if (this.value) {
          tzList.unshift({ label: 'Clear value', value: '' });
        }
        return tzList;
      }),
      tap(() => {
        this.menuTrigger?.menu?.resetActiveItem();
        setTimeout(() => {
          const input = this.filterInput.nativeElement;
          const inputLength = input.value.length;
          input.focus();
          input.select();
          input.setSelectionRange(inputLength, inputLength);
        });
      }),
    );
  }
  ngAfterViewInit(): void {
    this.openMenu();
  }
  preventEventAction(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }
  selectRecord(event: Event, timezone?: { value: string; label: string }): void {
    if (this.isInputLike) {
      this.valueChange.emit(timezone?.value);
      this.pickedVal = timezone?.label;
    } else {
      this.valueChange.emit({ actionType: 'add', timezone });
    }
    event.preventDefault();
  }
  clearValue(event: Event): void {
    this.selectRecord(event, undefined);
  }
  openMenu(): void {
    this.menuTrigger?.openMenu();
  }
}
